import { AccountHierarchyNested } from '@monetize/types/app';
import { useCallback, useMemo, useState } from 'react';
import { FaChevronRight } from 'react-icons/fa';
import {
  MBox,
  MFlex,
  MIcon,
  MIconButton,
  MTag,
  MTagLabel,
} from '../../../components/Monetize';
import { AccountLookupPopover } from '../../../components/Monetize/LookupPopover/AccountLookupPopover';

type AccountHierarchyNodeWithoutResponseType = Omit<
  AccountHierarchyNested,
  'responseType'
>;

interface AccountHierarchyTreeNodeProps {
  node: AccountHierarchyNodeWithoutResponseType;
  currentAccountId: string;
  depth: number;
  expandedNodes: Set<string>;
  onToggle: (nodeId: string) => void;
}

const AccountHierarchyTreeNode = ({
  node,
  currentAccountId,
  depth,
  expandedNodes,
  onToggle,
}: AccountHierarchyTreeNodeProps) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);
  const isCurrentAccount = node.id === currentAccountId;

  return (
    <MBox>
      <MFlex
        align="center"
        py={2}
        pl={`${depth * 12}px`}
        pr={2}
        borderRadius="md"
        _hover={{ bg: 'gray.50' }}
      >
        {hasChildren ? (
          <MIconButton
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
            icon={
              <MIcon
                as={FaChevronRight}
                w={3}
                h={3}
                transform={isExpanded ? 'rotate(90deg)' : undefined}
                transition="transform 0.2s ease"
              />
            }
            variant="ghost"
            size="xs"
            minW={6}
            h={6}
            onClick={() => onToggle(node.id)}
          />
        ) : (
          <MBox w={6} h={6} />
        )}
        <MBox mx={2}>
          <AccountLookupPopover value={node.id} displayLabel={node.name} />
        </MBox>
        {isCurrentAccount && (
          <MTag variant="blue" ml={2}>
            <MTagLabel>Current Account</MTagLabel>
          </MTag>
        )}
      </MFlex>
      {hasChildren && isExpanded && (
        <MBox>
          {node.children.map((child) => (
            <AccountHierarchyTreeNode
              key={child.id}
              node={child}
              currentAccountId={currentAccountId}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
            />
          ))}
        </MBox>
      )}
    </MBox>
  );
};

interface AccountHierarchyTreeProps {
  hierarchy: AccountHierarchyNested;
  currentAccountId: string;
}

const collectAllNodeIds = (
  node: AccountHierarchyNodeWithoutResponseType,
): string[] => {
  const ids = [node.id];
  if (node.children) {
    node.children.forEach((child) => {
      ids.push(...collectAllNodeIds(child));
    });
  }
  return ids;
};

export const AccountHierarchyTree = ({
  hierarchy,
  currentAccountId,
}: AccountHierarchyTreeProps) => {
  const allNodeIds = useMemo(() => collectAllNodeIds(hierarchy), [hierarchy]);

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    () => new Set(allNodeIds),
  );

  const handleToggle = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  return (
    <MBox p={2} maxH="400px" overflowY="auto">
      <AccountHierarchyTreeNode
        node={hierarchy}
        currentAccountId={currentAccountId}
        depth={0}
        expandedNodes={expandedNodes}
        onToggle={handleToggle}
      />
    </MBox>
  );
};
